import { useCallback, useState } from 'react';
import { errorMessage } from './api.ts';
import type { ToastOptions } from './useToast.ts';

/**
 * Shared runner for direct deck mutations (reorder, delete, duplicate,
 * sections, accent…). Chat proposals already funnel refresh through
 * Canvas's refreshSummary/bumpPreviewReload — this gives every direct UI
 * control the same single sync path, plus consistent error toasts and
 * optional optimistic state with revert.
 *
 * run(op, {
 *   refresh:   'summary' (default — manifest changed: refetch list + reload
 *              iframe) | 'preview' (slide file changed: reload iframe only)
 *              | 'none',
 *   optimistic / revert: apply local state immediately, roll back on error,
 *   toast:     string | (result) => string — success toast,
 *   undo:      (result) => async inverse op (renders Undo on the toast),
 * })
 * Resolves with the server result, or null on failure (already toasted).
 */
/** Longest a direct mutation may hang before we give up on it. `api` is a bare
 *  fetch with no timeout, so without this a request that never settles pins
 *  `busy` true forever — which disables every drag, ⋮ verb and section control
 *  in the editor until the user reloads the page. */
const MUTATION_TIMEOUT_MS = 30_000;

/** What to resync once the mutation lands. 'summary' refetches the slide list
 *  AND reloads the iframe (the manifest moved); 'preview' reloads the iframe
 *  only (a slide file changed). */
export type MutationRefresh = 'summary' | 'preview' | 'none';

export type MutationOptions<T> = {
  refresh?: MutationRefresh;
  /** Apply local state immediately; `revert` rolls it back on error. */
  optimistic?: () => void;
  revert?: () => void;
  /** `null` as well as absent: the call sites express "no toast here" as a
   *  ternary's else branch, which is more readable than `undefined`. */
  toast?: string | ((result: T) => string) | null;
  /** Renders Undo on the toast; receives what the mutation resolved with. */
  // Returns `unknown`: the result is awaited and discarded, so an inverse op
  // that happens to return something must not have to say `void`.
  undo?: ((result: T) => unknown) | null;
};

export type DeckMutationDeps = {
  refreshSummary: () => void;
  bumpPreviewReload: () => void;
  pushToast: (message: string, opts?: ToastOptions) => void;
};

export function useDeckMutation({ refreshSummary, bumpPreviewReload, pushToast }: DeckMutationDeps) {
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async <T>(
      op: () => Promise<T>,
      { refresh = 'summary', optimistic, revert, toast, undo }: MutationOptions<T> = {},
    ): Promise<T | null> => {
      if (optimistic) optimistic();
      setBusy(true);
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const result = await Promise.race([
          op(),
          new Promise<never>((_, reject) => {
            timer = setTimeout(
              () => reject(new Error('The server did not respond. Reloading the deck.')),
              MUTATION_TIMEOUT_MS,
            );
          }),
        ]);
        if (refresh === 'summary') refreshSummary();
        else if (refresh === 'preview') bumpPreviewReload();
        if (toast) {
          pushToast(typeof toast === 'function' ? toast(result) : toast, {
            undo: undo ? () => undo(result) : null,
          });
        }
        return result;
      } catch (err) {
        if (revert) revert();
        pushToast(errorMessage(err), { tone: 'error' });
        // Resync with disk. A failed mutation does not mean nothing landed:
        // a cross-section move is two requests, so the first can succeed while
        // the second throws, leaving the reverted UI describing a deck that no
        // longer exists. Refetching is cheap and always tells the truth.
        refreshSummary();
        return null;
      } finally {
        clearTimeout(timer);
        setBusy(false);
      }
    },
    [refreshSummary, bumpPreviewReload, pushToast],
  );

  return { run, busy };
}
